import { ApiError } from '@/api/http';
import { ApiErrorCodes } from '@/api/errors';
import type { Contact } from '@/api/types';
import { fromWire } from '@/api/wire';
import { isDavUnavailable, isReauthRequired } from '@/features/dav/failure';

/**
 * "This mailbox cannot have contacts at all" — told apart from "this request
 * failed".
 *
 * `unsupported_by_host` (no CardDAV configured for the mailbox's host),
 * `not_implemented` for a connected mailbox, and any route still a `501`
 * stub. None of these is an error the user can retry into success, and
 * rendering one as "Couldn't load contacts — Try again" would be a lie with a
 * button on it. A `409` asking for the mailbox's password again is not one of
 * these: it has a fix. The words, naming the mailbox, are in
 * `features/dav/failure.ts`.
 */
export function isNotAvailable(error: unknown): boolean {
  return isDavUnavailable(error);
}

/** True when the host answered but the feature is off, rather than unbuilt. */
export function isUnsupportedByHost(error: unknown): boolean {
  return error instanceof ApiError && error.is(ApiErrorCodes.UnsupportedByHost);
}

/** True for the CardDAV `If-Match` conflict: someone else changed the card. */
export function isConflict(error: unknown): boolean {
  // A connected mailbox whose password was rejected answers 409 too; that is a
  // password to re-enter, not someone else's version of the card.
  return error instanceof ApiError && error.status === 409 && !isReauthRequired(error);
}

/**
 * True for `428 Precondition Required`: the save carried no `If-Match` at all.
 *
 * Told apart from the `409` on purpose. A 409 is a decision for the user; a
 * 428 is this client failing to send the ETag it was read at, and answering it
 * with the conflict panel would offer a choice that cannot be acted on. It
 * shares the `conflict` code with the 409, so the status is what separates
 * them.
 */
export function isPreconditionRequired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 428;
}

/** The server's current ETag from a `409`, when it told us one. */
export function conflictEtag(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const value = error.details?.['current_etag'];
  return typeof value === 'string' ? value : undefined;
}

/**
 * The card as the server now holds it, from a `409`'s `details.theirs`.
 *
 * Error details are read raw (ADR 0009 keeps the envelope out of the case
 * conversion), so this one projection is converted here. Undefined when the
 * server could not read the card back — the panel then offers the whole-card
 * choice only.
 */
export function conflictTheirs(error: unknown): Contact | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const raw = error.details?.['theirs'];
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const theirs = fromWire<Contact>(raw);
  return typeof theirs.id === 'string' && Array.isArray(theirs.emails) ? theirs : undefined;
}
